import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useCallback } from 'react';
import { Upload, X, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface ImageDropzoneProps {
  onFileSelect: (file: File) => void;
  currentFile?: string;
  maxSizeMB?: number;
  acceptedFormats?: string[];
  label?: string;
  sublabel?: string;
  className?: string;
  onRemove?: () => void;
}

export const ImageDropzone: React.FC<ImageDropzoneProps> = ({
  onFileSelect,
  currentFile,
  maxSizeMB = 2,
  acceptedFormats = ['image/png', 'image/jpeg'],
  label = translateInterfaceText("Arraste seu arquivo ou clique para enviar"),
  sublabel = translateInterfaceText("PNG/JPG • Máx. 2MB"),
  className,
  onRemove
}) => {
  useLanguage();
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileInfo, setFileInfo] = useState<{ name: string; size: string } | null>(null);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' bytes';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const validateFile = (file: File): { valid: boolean; error?: string } => {
    // Check format
    if (!acceptedFormats.includes(file.type)) {
      return { valid: false, error: translateInterfaceText("Formato não suportado") };
    }

    // Check size
    const maxSize = maxSizeMB * 1024 * 1024;
    if (file.size > maxSize) {
      return { valid: false, error: `Arquivo maior que ${maxSizeMB}MB` };
    }

    return { valid: true };
  };

  const handleFile = useCallback((file: File) => {
    const validation = validateFile(file);
    
    if (!validation.valid) {
      setError(validation.error || translateInterfaceText("Erro ao processar arquivo"));
      return;
    }

    setError(null);
    setFileInfo({
      name: file.name,
      size: formatFileSize(file.size)
    });
    onFileSelect(file);
  }, [onFileSelect, acceptedFormats, maxSizeMB]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFile(file);
    }
  }, [handleFile]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
    }
  }, [handleFile]);

  const handleRemove = useCallback(() => {
    setFileInfo(null);
    setError(null);
    if (onRemove) {
      onRemove();
    }
  }, [onRemove]);

  return (
    <div className={cn("space-y-2", className)}>
      {!currentFile ? (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={cn(
            "border border-dashed rounded-xl bg-[#FAFBFC] p-4 transition-all cursor-pointer hover:border-primary/50",
            isDragging && "border-primary bg-primary/5",
            error && "border-destructive"
          )}
        >
          <label className="cursor-pointer flex flex-col items-center justify-center text-center">
            <input
              type="file"
              accept={acceptedFormats.join(',')}
              onChange={handleFileInput}
              className="hidden"
            />
            <Upload className={cn(
              "h-8 w-8 mb-2 text-muted-foreground",
              isDragging && "text-primary"
            )} />
            <p className="text-sm font-medium text-foreground mb-1">
              {label}
            </p>
            <p className="text-[11px] text-muted-foreground">
              {sublabel}
            </p>
          </label>
        </div>
      ) : (
        <div className="border border-[#D7DBE0] rounded-xl bg-[#FAFBFC] p-3 space-y-3">
          <img
            src={currentFile}
            alt={translateInterfaceText("Preview")}
            className="w-full max-h-32 object-contain rounded-lg"
          />
          
          {fileInfo && (
            <div className="text-xs font-normal text-muted-foreground">
              {fileInfo.name} • {fileInfo.size}
            </div>
          )}
          
          <div className="flex gap-2">
            <label className="flex-1">
              <input
                type="file"
                accept={acceptedFormats.join(',')}
                onChange={handleFileInput}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full"
                onClick={(e) => {
                  e.preventDefault();
                  (e.currentTarget.previousElementSibling as HTMLInputElement)?.click();
                }}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                {translateInterfaceText("Substituir")}</Button>
            </label>
            
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleRemove}
            >
              <X className="h-4 w-4 mr-2" />
              {translateInterfaceText("Remover")}</Button>
          </div>
        </div>
      )}
      
      {error && (
        <p className="text-xs text-destructive">
          ⚠️ {error}
        </p>
      )}
    </div>
  );
};
